import { CommonModule } from "@angular/common";
import { Component, EventEmitter, OnChanges, Output, SimpleChanges, computed, effect, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { catchError, forkJoin, of } from "rxjs";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@template-ui/types/ez-form.types";
import { EzDialogComponent } from "@template-ui/components/ez-dialog/ez-dialog.component";
import { EzDialogData } from "@template-ui/types/ez-dialog.types";
import { AppBaseComponent } from "@app/shared/components/app-base.component";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { PHMS_CASE_TERMINOLOGY } from "../../phms-case.terminology";
import { HearingPhaseBarDirective } from "../../hearing-detail/hearing-phase-bar.directive";
import { OperationsCaseCloseoutDto, OperationsCaseCompletionDto, PhmsIntakeDisplayError, WORKFLOW_RUNTIME_TRANSITION, mapPhmsIntakeError } from "./phms-case-lifecycle.models";
import { PhmsCaseLifecycleService } from "./phms-case-lifecycle.service";

@Component({
  selector: "phms-case-lifecycle", standalone: true,
  imports: [HearingPhaseBarDirective, CommonModule, UiLibraryModule, TerminologyIdentityDirective],
  templateUrl: "./phms-case-lifecycle.component.html", styleUrl: "./phms-case-lifecycle.component.scss",
})
export class PhmsCaseLifecycleComponent extends AppBaseComponent implements OnChanges {
  private readonly lifecycleService = inject(PhmsCaseLifecycleService);
  private readonly dialog = inject(MatDialog);
  private confirmation: MatDialogRef<EzDialogComponent> | null = null;
  private revision = 0;
  readonly caseUsage = PHMS_CASE_TERMINOLOGY.caseLifecycle;
  readonly caseText = createTerminologyUsageSignals(this.caseUsage);
  readonly workflowInstanceId = input.required<number>();
  readonly currentStatus = input<string | null | undefined>(null);
  @Output() closedStateChanged = new EventEmitter<boolean | null>();
  @Output() busyChanged = new EventEmitter<boolean>();
  readonly canTransition = this.roles.hasAnyPermissionSignal(WORKFLOW_RUNTIME_TRANSITION);
  readonly isLoading = signal(false);
  readonly isCompleting = signal(false);
  readonly isClosingOut = signal(false);
  readonly isConfirming = signal(false);
  readonly stateLoaded = signal(false);
  readonly completion = signal<OperationsCaseCompletionDto | null>(null);
  readonly closeout = signal<OperationsCaseCloseoutDto | null>(null);
  readonly errorState = signal<PhmsIntakeDisplayError | null>(null);
  readonly values = { completionReason: "", packageReference: "" };
  get completionReason(): string { return this.values.completionReason; }
  set completionReason(value: string) { this.values.completionReason = value; }
  get packageReference(): string { return this.values.packageReference; }
  set packageReference(value: string) { this.values.packageReference = value; }
  readonly isCompleted = computed(() => this.completion() !== null || this.currentStatus() === "completed");
  readonly isClosed = computed(() => this.closeout() !== null);
  readonly canCloseOut = computed(() => this.stateLoaded() && this.isCompleted() && !this.isClosed());
  readonly actionsBusy = computed(() => this.isLoading() || this.isCompleting() || this.isClosingOut() || this.isConfirming());
  readonly canAct = computed(() => this.canTransition() && this.workflowInstanceId() > 0 && this.stateLoaded() && !this.actionsBusy());
  readonly reasonConfig: InputConfig[] = [{ name: "completionReason", label: "Completion note (optional)", type: EzFormInputType.TEXTAREA, textAreaRows: 3, row: 1, width: "fill", floatLabel: "always" }];
  readonly packageConfig: InputConfig[] = [{ name: "packageReference", label: "Package reference (optional)", type: EzFormInputType.TEXT, row: 1, width: "fill", floatLabel: "always" }];

  constructor() {
    super();
    effect(() => this.busyChanged.emit(this.actionsBusy()));
    effect(() => this.closedStateChanged.emit(this.stateLoaded() ? this.isClosed() : null));
    this.destroyRef.onDestroy(() => { this.confirmation?.close(false); this.busyChanged.emit(false); });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflowInstanceId']) {
      this.revision++;
      this.confirmation?.close(false);
      this.completion.set(null); this.closeout.set(null);
      this.values.completionReason = ""; this.values.packageReference = "";
      this.isCompleting.set(false); this.isClosingOut.set(false);
      this.reflectTerminalState();
    }
  }
  onInputChange(event: InputChangeEvent): void {
    if (!this.canAct()) return;
    if (event.name === "completionReason" && !this.isCompleted() && !this.isClosed()) this.values.completionReason = String(event.value ?? "");
    if (event.name === "packageReference" && !this.isClosed()) this.values.packageReference = String(event.value ?? "");
  }
  reflectTerminalState(): void {
    if (this.isCompleting() || this.isClosingOut() || this.isConfirming()) return;
    const id = this.workflowInstanceId(); const revision = ++this.revision;
    this.stateLoaded.set(false); this.errorState.set(null);
    if (id <= 0) return;
    this.isLoading.set(true);
    forkJoin({ completion: this.lifecycleService.getCompletion(id), closeout: this.lifecycleService.getCloseoutPackage(id) }).pipe(
      catchError(error => { if (revision === this.revision) this.errorState.set(mapPhmsIntakeError(error)); return of(null); }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      if (revision !== this.revision) return;
      this.isLoading.set(false);
      if (result) { this.completion.set(result.completion); this.closeout.set(result.closeout); this.stateLoaded.set(true); }
    });
  }
  completeCase(): void { if (this.canAct() && !this.isCompleted() && !this.isClosed()) this.confirmAction(false); }
  closeoutCase(): void { if (this.canAct() && this.canCloseOut()) this.confirmAction(true); }
  private confirmAction(close: boolean): void {
    const id = this.workflowInstanceId(); const revision = this.revision;
    const value = (close ? this.packageReference : this.completionReason).trim();
    const escaped = value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const heading = close ? "Freeze closeout package" : this.caseText.complete();
    const data: EzDialogData = {
      heading, headerStyle: "info",
      question: close ? "Freeze the final closeout package? The saved package cannot be edited. This requires a completed case and an approved, finalized decision." : "Complete this case? Post-Hearing review and required workflow approvals must be complete. This completes the case workflow.",
      content: `<p>${close ? "Package reference" : "Completion note"}: <span style="white-space: pre-wrap; overflow-wrap: anywhere">${escaped || (close ? "Generated automatically" : "None entered")}</span></p>`,
      buttons: [{ text: "Cancel", action: ref => ref.close(false) }, { text: close ? "Confirm & freeze" : "Confirm & complete", action: ref => ref.close(true) }],
    };
    this.isConfirming.set(true);
    const ref = this.dialog.open(EzDialogComponent, { width: "38rem", maxWidth: "calc(100vw - 2rem)", maxHeight: "85vh", ariaLabel: heading, autoFocus: "first-tabbable", restoreFocus: true, data });
    this.confirmation = ref;
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      this.confirmation = null; this.isConfirming.set(false);
      if (confirmed !== true || revision !== this.revision || !this.canAct() || this.isClosed() || (close ? !this.canCloseOut() : this.isCompleted())) return;
      this.errorState.set(null);
      if (close) {
        this.isClosingOut.set(true);
        this.lifecycleService.closeoutCase(id, { packageReference: value }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: dto => { if (revision === this.revision) { this.closeout.set(dto); this.values.packageReference = ""; this.isClosingOut.set(false); } },
          error: error => { if (revision === this.revision) { this.errorState.set(mapPhmsIntakeError(error)); this.isClosingOut.set(false); } },
        });
      } else {
        this.isCompleting.set(true);
        this.lifecycleService.completeCase(id, { reason: value }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: dto => { if (revision === this.revision) { this.completion.set(dto); this.values.completionReason = ""; this.isCompleting.set(false); } },
          error: error => { if (revision === this.revision) { this.errorState.set(mapPhmsIntakeError(error)); this.isCompleting.set(false); } },
        });
      }
    });
  }
}
